import React from 'react'
import {
  Box,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Paper,
} from '@mui/material'
import { theme } from 'common/theme'

interface GenericErrorProps {
  error: any
  onRetry: () => void
}

function GenericError({ error, onRetry }: GenericErrorProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        mt: 4,
      }}
    >
      <Paper sx={{ p: 4 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: '500px',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: `${theme.palette.error.main}15`,
              mb: 3,
              position: 'relative',
              '&::before': {
                content: '"!"',
                fontSize: 40,
                fontWeight: 'bold',
                color: theme.palette.error.main,
                position: 'absolute',
              },
            }}
          />

          <Typography
            variant='h5'
            sx={{
              fontWeight: 600,
              mb: 2,
              color: theme.palette.common.black,
            }}
          >
            Something went wrong
          </Typography>

          <Alert
            severity='error'
            sx={{
              width: '100%',
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-message': {
                width: '100%',
              },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600, mb: 1 }}>
              Something went wrong
            </AlertTitle>
            <Typography variant='body2' sx={{ mb: 1 }}>
              {error?.message ||
                error?.toString() ||
                JSON.stringify(error, null, 2) ||
                error}
            </Typography>
          </Alert>

          <Box
            sx={{
              backgroundColor: theme.palette.grey.A100,
              borderRadius: 2,
              p: 3,
              mb: 3,
              width: '100%',
            }}
          >
            <Typography variant='body2' sx={{ mb: 2, fontWeight: 500 }}>
              What you can do:
            </Typography>
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant='body2' sx={{ mb: 1 }}>
                • Check your internet connection
              </Typography>
              <Typography variant='body2' sx={{ mb: 1 }}>
                • Try again in a few minutes
              </Typography>
              <Typography variant='body2' sx={{ mb: 1 }}>
                • Refresh the page and start over
              </Typography>
              <Typography variant='body2'>
                • Contact support if the issue continues. Please include the
                error details above.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <Button
              variant='contained'
              color='secondary'
              onClick={onRetry}
              sx={{
                flex: 1,
              }}
            >
              Try Again
            </Button>
            <Button
              variant='text'
              component='a'
              href={`mailto:support@xtramath.org?subject=Student%20Error&body=Error%20details:%20${encodeURIComponent(
                error?.message || error?.toString() || JSON.stringify(error)
              )}`}
              sx={{ flex: 1, color: theme.palette.common.black }}
            >
              Contact Support
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

export default GenericError
